'use client';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GrafanaHealthView } from '@/components/GrafanaHealthView';
import { GoldenCheetahWorkoutView } from '@/components/GoldenCheetahWorkoutView';
import { AdvancedSportScience } from '@/components/AdvancedSportScience';
import { PredictiveView } from '@/components/PredictiveView';
import { TrailRunMapView } from '@/components/TrailRunMapView';
import { GarminUploader } from '@/components/GarminUploader';
import { PocketBaseSync } from '@/components/PocketBaseSync';
import { Activity, BarChart3, HeartPulse, TrendingUp, Cloud, Map as MapIcon, Bike, Footprints, Waves, Flame, Dumbbell, Settings } from 'lucide-react';
import { ModeToggle } from '@/components/mode-toggle';
import { PowerProfileView } from '@/components/PowerProfileView';
import { useEffect, useMemo, useState } from 'react';
import { ParsedFitResult } from '@/lib/fitParser';
import type { WellnessRecord, ActivityMetric } from '@/lib/pbSync';
import localforage from 'localforage';
import { AthleteSettingsForm } from '@/components/AthleteSettingsModal';
import { AthleteSettings, getAthleteSettings, DEFAULT_SETTINGS } from '@/lib/athleteSettings';
import { pb } from '@/lib/pb';
import { Login } from '@/components/Login';

const RANGES = [
  { label: '7D',  days: 7   },
  { label: '4W',  days: 28  },
  { label: '3M',  days: 90  },
  { label: '6M',  days: 180 },
  { label: '1Y',  days: 365 },
  { label: 'All', days: 0   },
] as const;

type RangeLabel = typeof RANGES[number]['label'];

function filterByDays<T extends { startTime?: Date; date?: string }>(
  items: T[], days: number, key: 'startTime' | 'date'
): T[] {
  if (days === 0) return items;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  if (key === 'startTime') {
    return (items as any[]).filter((w: any) => w.startTime >= cutoff);
  }
  const cutStr = cutoff.toISOString().split('T')[0];
  return (items as any[]).filter((w: any) => w.date >= cutStr);
}

export default function DashboardPage() {
  const [realData,     setRealData]     = useState<ParsedFitResult[] | null>(null);
  const [wellnessData, setWellnessData] = useState<WellnessRecord[]>([]);
  const [metricsData,  setMetricsData]  = useState<ActivityMetric[]>([]);
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [range, setRange] = useState<RangeLabel>('3M');
  const [athleteSettings, setAthleteSettings] = useState<AthleteSettings>(DEFAULT_SETTINGS);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state
  useEffect(() => {
    setIsAuthenticated(pb.authStore.isValid);
  }, []);

  // Load from IndexedDB on mount
  useEffect(() => {
    (async () => {
      try {
        const [cached, wellness, metrics, settings] = await Promise.all([
          localforage.getItem<ParsedFitResult[]>('workout_cache'),
          localforage.getItem<WellnessRecord[]>('wellness_cache'),
          localforage.getItem<ActivityMetric[]>('metrics_cache'),
          getAthleteSettings(),
        ]);
        if (cached?.length)   setRealData(cached.map(w => ({ ...w, startTime: new Date(w.startTime) })));
        if (wellness?.length) setWellnessData(wellness);
        if (metrics?.length)  setMetricsData(metrics);
        if (settings) setAthleteSettings(settings);
      } catch (err) {
        console.error('Failed to init dashboard', err);
      }
      setIsLoadingCache(false);
    })();
  }, []);

  // Close details dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const details = document.querySelectorAll('details[open]');
      details.forEach(d => {
        if (!d.contains(e.target as Node)) {
          d.removeAttribute('open');
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Smart merge: never discard existing cached records when re-syncing ──────
  const handleActivitiesSync = (fresh: ParsedFitResult[]) => {
    setRealData(prev => {
      const map = new Map<string, ParsedFitResult>();
      const valid = (w: ParsedFitResult) =>
        w.startTime instanceof Date && !isNaN(w.startTime.getTime());
      // Existing cache first, fresh data overwrites duplicates
      (prev ?? []).filter(valid).forEach(w => map.set(w.startTime.toISOString(), w));
      fresh.filter(valid).forEach(w => map.set(w.startTime.toISOString(), w));
      const merged = Array.from(map.values())
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      localforage.setItem('workout_cache', merged).catch(console.error);
      return merged;
    });
  };

  const handleWellnessSync = (data: WellnessRecord[]) => {
    setWellnessData(data);
    localforage.setItem('wellness_cache', data).catch(console.error);
  };

  const handleMetricsSync = (data: ActivityMetric[]) => {
    setMetricsData(data);
    localforage.setItem('metrics_cache', data).catch(console.error);
  };

  const days = RANGES.find(r => r.label === range)?.days ?? 0;

  const filteredData     = useMemo(() => realData     ? filterByDays(realData     as any, days, 'startTime') as ParsedFitResult[] : null, [realData, days]);
  const filteredWellness = useMemo(() => filterByDays(wellnessData as any, days, 'date')  as WellnessRecord[],  [wellnessData, days]);
  const filteredMetrics  = useMemo(() => filterByDays(metricsData  as any, days, 'date')  as ActivityMetric[],  [metricsData,  days]);

  const hiddenCount = realData && filteredData ? realData.length - filteredData.length : 0;

  if (!isAuthenticated) {
    return <Login onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <main className="min-h-screen bg-background text-foreground">

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/85 backdrop-blur-xl">
        {/* Row 1: logo + actions */}
        <div className="max-w-screen-xl mx-auto px-2.5 sm:px-4 h-12 flex items-center justify-between gap-1.5 sm:gap-2">
          <div className="flex items-center gap-2 flex-shrink-0 group cursor-default">
            <div className="w-7 h-7 rounded-lg bg-foreground flex items-center justify-center shadow-sm transition-transform group-hover:scale-105">
              <Activity className="w-4 h-4 text-background" />
            </div>
            <span className="hidden sm:block text-sm font-semibold tracking-tight">Athletics Dashboard</span>
          </div>

          {/* Range picker — always visible, smaller on mobile */}
          <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-0.5">
            {RANGES.map(r => (
              <button
                key={r.label}
                onClick={() => setRange(r.label)}
                className={`px-2 sm:px-3 py-1 rounded-md text-xs font-medium transition-all duration-150
                  ${range === r.label
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            {!isLoadingCache && (
              <div className="relative">
                <details className="group [&_summary::-webkit-details-marker]:hidden">
                  <summary className="w-8 h-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground flex items-center justify-center cursor-pointer list-none outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <Settings className="w-4 h-4" />
                  </summary>
                  <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[350px] p-4 rounded-xl border border-border/60 bg-muted/95 backdrop-blur-xl shadow-lg z-50">
                    <AthleteSettingsForm onSave={setAthleteSettings} onComplete={() => {
                      const details = document.activeElement?.closest('details');
                      if (details) details.removeAttribute('open');
                    }} />
                  </div>
                </details>
              </div>
            )}
            {!isLoadingCache && (
              <div className="relative">
                <details className="group [&_summary::-webkit-details-marker]:hidden">
                  <summary className="w-8 h-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground flex items-center justify-center cursor-pointer list-none outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <Cloud className="w-4 h-4" />
                  </summary>
                  <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[600px] max-w-2xl p-4 rounded-xl border border-border/60 bg-muted/95 backdrop-blur-xl shadow-lg z-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <GarminUploader onUploadComplete={handleActivitiesSync} />
                      <PocketBaseSync
                        onSyncComplete={handleActivitiesSync}
                        onWellnessSync={handleWellnessSync}
                        onMetricsSync={handleMetricsSync}
                      />
                    </div>
                  </div>
                </details>
              </div>
            )}
            <ModeToggle />
            <button
              onClick={() => {
                pb.authStore.clear();
                setIsAuthenticated(false);
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden text-[10px]">Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-screen-xl mx-auto px-3 py-4 sm:px-6 sm:py-8 space-y-6 sm:space-y-8">

        {hiddenCount > 0 && (
          <p className="text-xs text-muted-foreground">
            Showing{' '}
            <span className="font-medium text-foreground">{filteredData?.length ?? 0}</span>
            {' '}of{' '}
            <span className="font-medium text-foreground">{realData?.length ?? 0}</span>
            {' '}activities.{' '}
            <button className="underline underline-offset-2 hover:text-foreground transition-colors" onClick={() => setRange('All')}>
              Show all
            </button>
          </p>
        )}


        <Tabs defaultValue="sportscience" className="w-full">
          {/* Tabs scroll horizontally on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-muted/50 p-1 rounded-xl h-auto gap-0.5 sm:gap-1 w-max sm:w-auto">
              <TabsTrigger value="sportscience" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <BarChart3 className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Training Load</span>
              </TabsTrigger>
              <TabsTrigger value="cycling" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <Bike className="w-3.5 h-3.5 sm:mr-1.5 inline-block" /><span className="hidden sm:inline">Cycling</span>
              </TabsTrigger>
              <TabsTrigger value="running" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <Footprints className="w-3.5 h-3.5 sm:mr-1.5 inline-block" /><span className="hidden sm:inline">Running</span>
              </TabsTrigger>
              <TabsTrigger value="swimming" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <Waves className="w-3.5 h-3.5 sm:mr-1.5 inline-block" /><span className="hidden sm:inline">Swimming</span>
              </TabsTrigger>
              <TabsTrigger value="other" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <Flame className="w-3.5 h-3.5 sm:mr-1.5 inline-block" /><span className="hidden sm:inline">HIIT &amp; More</span>
              </TabsTrigger>
              <TabsTrigger value="grafana" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <HeartPulse className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Health &amp; Sleep</span>
              </TabsTrigger>
              <TabsTrigger value="predictive" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <TrendingUp className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Predictive</span>
              </TabsTrigger>
              <TabsTrigger value="trailruns" className="rounded-lg text-xs font-medium px-2 sm:px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all hover:bg-background/40 hover:text-foreground">
                <MapIcon className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Maps</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="mt-6">
            <TabsContent value="sportscience" className="m-0 focus-visible:outline-none">
              <AdvancedSportScience realData={filteredData} wellnessData={filteredWellness} metricsData={filteredMetrics} />
            </TabsContent>
            <TabsContent value="cycling" className="m-0 focus-visible:outline-none">
              <GoldenCheetahWorkoutView realData={filteredData} metricsData={filteredMetrics} sportFilter="cycl" hideAggregates={true} athleteSettings={athleteSettings} />
            </TabsContent>
            <TabsContent value="running" className="m-0 focus-visible:outline-none">
              <GoldenCheetahWorkoutView realData={filteredData} metricsData={filteredMetrics} sportFilter="run" hideAggregates={true} athleteSettings={athleteSettings} />
            </TabsContent>
            <TabsContent value="swimming" className="m-0 focus-visible:outline-none">
              <GoldenCheetahWorkoutView realData={filteredData} metricsData={filteredMetrics} sportFilter="swim" hideAggregates={true} athleteSettings={athleteSettings} />
            </TabsContent>
            <TabsContent value="other" className="m-0 focus-visible:outline-none">
              <GoldenCheetahWorkoutView realData={filteredData} metricsData={filteredMetrics} sportFilter="other" hideAggregates={true} athleteSettings={athleteSettings} />
            </TabsContent>

            <TabsContent value="grafana" className="m-0 focus-visible:outline-none">
              <GrafanaHealthView realData={filteredData} wellnessData={filteredWellness} />
            </TabsContent>
            <TabsContent value="predictive" className="m-0 focus-visible:outline-none">
              <PredictiveView realData={filteredData} metricsData={filteredMetrics} />
            </TabsContent>
            <TabsContent value="trailruns" className="m-0 focus-visible:outline-none">
              <TrailRunMapView realData={filteredData} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </main>
  );
}
